import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { Container, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faCheck } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { setOrderType } from "../../redux/orderTypeSlice";
import { setSavedSelectedCart } from "../../redux/cartSlice";
import { RootState } from "../../redux/store";
import "./index.css";

const mapContainerStyle = {
    width: "100%",
    height: "calc(100vh - 280px)",
    borderRadius: "0",
};

const GOOGLE_MAPS_LIBRARIES: ("places" | "geometry" | "marker")[] = ["places", "geometry", "marker"];

interface LocationState {
    selectedCartId: string;
}

const PickupMapConfirm: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const state = location.state as LocationState;

    const [selectedCartId, setSelectedCartId] = useState<string | null>(null);
    const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({ lat: 6.9270786, lng: 79.861243 });
    const mapRef = useRef<google.maps.Map | null>(null);
    const markersRef = useRef<Map<string, google.maps.marker.AdvancedMarkerElement>>(new Map());

    const cartList = useSelector((state: RootState) => state.cartList.carts);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey:
            process.env.REACT_APP_GOOGLE_MAPS_API_KEY! ||
            "AIzaSyD_zEfLidcypXTaAVeiQVX5PHlrcj6xEpM",
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    // Filter carts to show only Online and Approved carts
    const filteredCartList = cartList?.filter(
        (cart: any) => cart.cartState === "Online" && cart.status === "Approved" && cart.isFixedLocation === true
    ) || [];

    // const iconURL = "https://storage.googleapis.com/fooddelivery-7eac6.appspot.com/Icons/cart-icon.png";
    // const iconURL2 = icon;

    useEffect(() => {
        if (!state || !state.selectedCartId) {
            // If no state, redirect back
            navigate("/order");
            return;
        }
        setSelectedCartId(state.selectedCartId);

        // Find selected cart and center map on it
        const selectedCart = filteredCartList.find((cart: any) => cart.id === state.selectedCartId);
        if (selectedCart?.location) {
            setMapCenter({
                lat: selectedCart.location._latitude,
                lng: selectedCart.location._longitude
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state?.selectedCartId, navigate]);

    // Create/update markers when map is loaded or cart list changes
    useEffect(() => {
        if (!mapRef.current || !window.google?.maps?.marker?.AdvancedMarkerElement) {
            return;
        }

        // Clear existing markers
        markersRef.current.forEach((marker) => {
            marker.map = null;
        });
        markersRef.current.clear();

        // Create new markers for all carts
        filteredCartList.forEach((cart: any) => {
            const isSelected = cart.id === selectedCartId;
            const pinElement = new window.google.maps.marker.PinElement({
                background: isSelected ? "#28a745" : "#DC3545",
                borderColor: "#FFFFFF",
                glyphColor: "#FFFFFF",
                scale: isSelected ? 1.5 : 1.2,
            });

            const marker = new window.google.maps.marker.AdvancedMarkerElement({
                map: mapRef.current,
                position: {
                    lat: cart.location._latitude,
                    lng: cart.location._longitude
                },
                content: pinElement.element,
                title: cart.displayName || cart.name,
            });

            marker.addListener('click', () => {
                handleCartSelect(cart.id);
            });

            markersRef.current.set(cart.id, marker);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredCartList, selectedCartId]);

    const handleBack = () => {
        navigate(-1);
    };

    const handleCartSelect = (cartId: string) => {
        setSelectedCartId(cartId);
        const selectedCart = filteredCartList.find((cart: any) => cart.id === cartId);
        if (selectedCart?.location) {
            const newCenter = {
                lat: selectedCart.location._latitude,
                lng: selectedCart.location._longitude
            };
            setMapCenter(newCenter);
            // Pan to the cart
            if (mapRef.current) {
                mapRef.current.panTo(newCenter);
            }
        }
    };

    const handleConfirmAndContinue = () => {
        if (!selectedCartId) {
            return;
        }

        // Find the selected cart details
        const selectedCart = filteredCartList.find((cart: any) => cart.id === selectedCartId);

        if (selectedCart) {
            // Save selected cart to Redux
            dispatch(setSavedSelectedCart(selectedCart));

            // Set order type to Pickup
            dispatch(setOrderType({ orderType: "Pickup", cartId: selectedCartId }));
        }

        // Navigate to menu page
        navigate("/order");
    };

    const selectedCartDetails = filteredCartList.find((cart: any) => cart.id === selectedCartId);

    if (!isLoaded) {
        return <div>Loading map...</div>;
    }

    return (
        <div className="pickup-map-confirm-page">
            <Container fluid className="p-0">
                <div className="pickup-map-header">
                    <div className="pickup-location-info">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div>
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-success me-2" size="lg" />
                                <span className="outlet-label">OUTLET LIST</span>
                            </div>
                            <button className="search-again-btn" onClick={handleBack}>
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                Search again
                            </button>
                        </div>
                    </div>
                </div>

                <div className="pickup-content-layout">
                    {/* Left Side - Cart List */}
                    <div className="pickup-cart-list-panel">
                        <div className="cart-list-scroll">
                            {filteredCartList && filteredCartList.length > 0 ? (
                                filteredCartList.map((cart: any, index: number) => (
                                    <div
                                        key={cart.id}
                                        className={`pickup-cart-item ${selectedCartId === cart.id ? "selected" : ""}`}
                                        onClick={() => handleCartSelect(cart.id)}
                                    >
                                        <div className="cart-number">{index + 1}</div>
                                        <div className="cart-info">
                                            <h5 className="cart-name">{cart.displayName || cart.name}</h5>
                                            {cart.address && <p className="cart-address">{cart.address}</p>}
                                            <div className="cart-meta">
                                                <span className="cart-distance">
                                                    {cart.loadingConfigurations?.distance
                                                        ? `${cart.loadingConfigurations.distance.toFixed(1)} km`
                                                        : 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                        {/* <Button
                                            variant="success"
                                            size="sm"
                                            className="cart-order-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCartSelect(cart.id);
                                            }}
                                        >
                                            Select
                                        </Button> */}
                                    </div>
                                ))
                            ) : (
                                <div className="no-carts-message">
                                    <p>No pickup locations available at the moment.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Side - Map */}
                    <div className="pickup-map-panel">
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={mapCenter}
                            zoom={13}
                            onLoad={(map) => {
                                mapRef.current = map;
                            }}
                            onUnmount={() => {
                                // Clean up markers
                                markersRef.current.forEach((marker) => {
                                    marker.map = null;
                                });
                                markersRef.current.clear();
                                mapRef.current = null;
                            }}
                            options={{
                                mapId: "curry-king-pickup-map",
                                zoomControl: true,
                                streetViewControl: false,
                                mapTypeControl: false,
                                fullscreenControl: true,
                            }}
                        >
                        </GoogleMap>
                        {selectedCartDetails && (
                            <div className="pickup-map-footer">
                                <div className="selected-cart-info">
                                    <div className="d-flex align-items-start">
                                        <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                                        <div>
                                            <h6 className="mb-1">{selectedCartDetails.displayName || selectedCartDetails.name}</h6>
                                            {selectedCartDetails.address && (
                                                <p className="mb-0 text-muted small">{selectedCartDetails.address}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex gap-3">
                                    <Button
                                        variant="warning"
                                        className="back-btn"
                                        onClick={handleBack}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        variant="success"
                                        className="confirm-btn flex-grow-1"
                                        onClick={handleConfirmAndContinue}
                                    >
                                        Confirm & Continue
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default PickupMapConfirm;
